import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import type { ApiResponse } from '@vision/ui/interfaces';
import { buildEndpointWithQuery, createHttpExtraOptions } from '@vision/ui/utils';
import { axiosBaseQuery } from './base.service';
import { ActionLogResponseDto, normalizeActionLogsResponse } from './action-logs.service.mapper';
import type { ActionLog, ActionLogMeta } from './action-logs.types';

type NodeQueryRequest = { nodeId: string; query: string };

function withNodeId(query: string, nodeId: string): string {
  const nodeParam = `node_id=${encodeURIComponent(nodeId)}`;
  return query ? `${query}&${nodeParam}` : nodeParam;
}

export const ActionLogsService = createApi({
  reducerPath: 'actionLogsApi',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    unifiedActionLogs: builder.query<ApiResponse<ActionLog[], ActionLogMeta>, NodeQueryRequest>({
      query: ({ nodeId, query }) => ({
        url: buildEndpointWithQuery('/v1/unified_action_logs', withNodeId(query, nodeId)),
        method: HttpMethod.GET,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleError: false,
          },
        }),
      }),
      transformResponse: (response: ApiResponse<ActionLogResponseDto[], ActionLogMeta>) =>
        normalizeActionLogsResponse(response),
    }),
    unifiedActionLogsExport: builder.query<Blob, NodeQueryRequest>({
      query: ({ nodeId, query }) => ({
        url: buildEndpointWithQuery('/v1/unified_action_logs/export', withNodeId(query, nodeId)),
        method: HttpMethod.GET,
        responseType: 'blob',
      }),
    }),
  }),
});

export const { useLazyUnifiedActionLogsQuery, useLazyUnifiedActionLogsExportQuery } = ActionLogsService;
